/**
 * Category-level editorial hero images for insight articles.
 *
 * Every article inherits a photograph matching its category, so no blog post
 * ships without a visual (better dwell time) and every article exposes a real
 * og:image / twitter:image instead of the generic site-wide fallback.
 * A post that declares its own `heroImage` always wins.
 */
import notary from "@/assets/insights/notary.jpg";
import translation from "@/assets/insights/translation.jpg";
import legalization from "@/assets/insights/legalization.jpg";
import marriage from "@/assets/insights/marriage.jpg";
import visa from "@/assets/insights/visa.jpg";
import guide from "@/assets/insights/guide.jpg";

export type InsightCategoryKey =
  | "notary"
  | "translation"
  | "legalization"
  | "marriage"
  | "visa"
  | "guide";

export interface InsightHero {
  src: string;
  altTh: string;
  altEn: string;
  width: number;
  height: number;
}

export const INSIGHT_HERO_IMAGES: Record<InsightCategoryKey, InsightHero> = {
  notary: {
    src: notary,
    altTh: "โต๊ะทำงานทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) พร้อมตราประทับรับรองเอกสาร",
    altEn: "Notarial Services Attorney desk in Thailand with official documents and notary stamp",
    width: 1200,
    height: 630,
  },
  translation: {
    src: translation,
    altTh: "โต๊ะทำงานนักแปลเอกสารรับรอง เปรียบเทียบเอกสารไทยกับคำแปลภาษาอังกฤษ",
    altEn: "Certified translator workspace comparing a Thai document with its English translation",
    width: 1200,
    height: 630,
  },
  legalization: {
    src: legalization,
    altTh: "เคาน์เตอร์บริการรับรองนิติกรณ์เอกสาร พร้อมแฟ้มเอกสารรอยื่นกรมการกงสุล",
    altEn: "Consular legalization service counter with document folders awaiting MFA certification",
    width: 1200,
    height: 630,
  },
  marriage: {
    src: marriage,
    altTh: "โต๊ะจดทะเบียนสมรสที่สำนักงานเขต/อำเภอ พร้อมทะเบียนสมรสและแหวนแต่งงาน",
    altEn: "Thai district office marriage registration desk with registry book and wedding rings",
    width: 1200,
    height: 630,
  },
  visa: {
    src: visa,
    altTh: "หนังสือเดินทางและแบบฟอร์มคำขอวีซ่าบนโต๊ะที่ปรึกษาด้านวีซ่าและใบอนุญาตทำงาน",
    altEn: "Passport and visa application forms on a Thailand visa consultant's desk",
    width: 1200,
    height: 630,
  },
  guide: {
    src: guide,
    altTh: "ทีมที่ปรึกษากำลังตรวจเช็กลิสต์เอกสารกับลูกค้าในห้องประชุม",
    altEn: "Consulting team reviewing a document checklist with a client in a meeting room",
    width: 1200,
    height: 630,
  },
};

/** Absolute URL for og:image / twitter:image (hashed asset path + site origin). */
export function insightHeroAbsolute(
  category: InsightCategoryKey,
  siteUrl: string,
): string {
  const src = INSIGHT_HERO_IMAGES[category].src;
  return src.startsWith("http") ? src : `${siteUrl}${src}`;
}